import { EnumHelperBase, type EnumDataObject } from './enum.helpers';

export enum COUNTRY {
	CZE = 1,
	SVK = 2,
	AUT = 3,
	GBR = 4,
	SRB = 5,
	BEL = 6,
	BGR = 7,
	HRV = 8,
	CYP = 9,
	DNK = 10,
	EST = 11,
	FIN = 12,
	FRA = 13,
	DEU = 14,
	GRC = 15,
	HUN = 16,
	IRL = 17,
	ITA = 18,
	LVA = 19,
	LTU = 20,
	LUX = 21,
	MLT = 22,
	POL = 23,
	PRT = 24,
	ROU = 25,
	SVN = 26,
	ESP = 27,
	SWE = 28,
	NLD = 29,
	ISL = 30,
	LIE = 31,
	NOR = 32,
	CHE = 33,
}

export interface CountryEnumBaseData extends EnumDataObject<CountryEnumBaseExtraData, COUNTRY, 'countryId'> {}

export interface CountryEnumBaseExtraData {
	isoChar2: string;
	isoChar3: string;
	isoNum: string;
	partOfEu: boolean;
	vatIdRegEx?: RegExp;
	taxIdRegEx?: RegExp;
	systemWideSupport: boolean;
}

/**
 * Base Country enumeration
 * @see https://github.com/NFCtron/nfctron-db/blob/dev/diagrams/global.png
 */
export class CountryEnumBase extends EnumHelperBase<CountryEnumBaseExtraData, COUNTRY, 'country', 'countryId'> {
	constructor() {
		super(
			'country',
			{
				[COUNTRY.CZE]: {
					isoChar2: 'CZ',
					isoChar3: 'CZE',
					isoNum: '203',
					partOfEu: true,
					systemWideSupport: true,
					vatIdRegEx: /^(CZ)[0-9]{8,10}$/,
				},
				[COUNTRY.SVK]: {
					isoChar2: 'SK',
					isoChar3: 'SVK',
					isoNum: '703',
					partOfEu: true,
					systemWideSupport: true,
					vatIdRegEx: /^(SK)[0-9]{10}$/,
					taxIdRegEx: /^[0-9]{9,10}$/,
				},
				[COUNTRY.AUT]: {
					isoChar2: 'AT',
					isoChar3: 'AUT',
					isoNum: '040',
					partOfEu: true,
					systemWideSupport: true,
					vatIdRegEx: /^(AT)U[0-9]{8}$/,
				},
				[COUNTRY.GBR]: {
					isoChar2: 'GB',
					isoChar3: 'GBR',
					isoNum: '826',
					partOfEu: false,
					systemWideSupport: false,
				},
				[COUNTRY.SRB]: {
					isoChar2: 'RS',
					isoChar3: 'SRB',
					isoNum: '688',
					partOfEu: false,
					systemWideSupport: true,
				},
				[COUNTRY.BEL]: {
					isoChar2: 'BE',
					isoChar3: 'BEL',
					isoNum: '056',
					partOfEu: true,
					vatIdRegEx: /^(BE)[0-9]{10}$/,
					systemWideSupport: false,
				},
				[COUNTRY.BGR]: {
					isoChar2: 'BG',
					isoChar3: 'BGR',
					isoNum: '100',
					partOfEu: true,
					vatIdRegEx: /^(BG)[0-9]{9,10}$/,
					systemWideSupport: false,
				},
				[COUNTRY.HRV]: {
					isoChar2: 'HR',
					isoChar3: 'HRV',
					isoNum: '191',
					partOfEu: true,
					vatIdRegEx: /^(HR)[0-9]{11}$/,
					systemWideSupport: false,
				},
				[COUNTRY.CYP]: {
					isoChar2: 'CY',
					isoChar3: 'CYP',
					isoNum: '196',
					partOfEu: true,
					vatIdRegEx: /^(CY)[0-9]{8}[A-Z]$/,
					systemWideSupport: false,
				},
				[COUNTRY.DNK]: {
					isoChar2: 'DK',
					isoChar3: 'DNK',
					isoNum: '208',
					partOfEu: true,
					vatIdRegEx: /^(DK)[0-9]{8}$/,
					systemWideSupport: false,
				},
				[COUNTRY.EST]: {
					isoChar2: 'EE',
					isoChar3: 'EST',
					isoNum: '233',
					partOfEu: true,
					vatIdRegEx: /^(EE)[0-9]{9}$/,
					systemWideSupport: false,
				},
				[COUNTRY.FIN]: {
					isoChar2: 'FI',
					isoChar3: 'FIN',
					isoNum: '246',
					partOfEu: true,
					vatIdRegEx: /^(FI)[0-9]{8}$/,
					systemWideSupport: false,
				},
				[COUNTRY.FRA]: {
					isoChar2: 'FR',
					isoChar3: 'FRA',
					isoNum: '250',
					partOfEu: true,
					vatIdRegEx: /^(FR)[0-9A-Z]{2}[0-9]{9}$/,
					systemWideSupport: false,
				},
				[COUNTRY.DEU]: {
					isoChar2: 'DE',
					isoChar3: 'DEU',
					isoNum: '276',
					partOfEu: true,
					vatIdRegEx: /^(DE)[0-9]{9}$/,
					systemWideSupport: true,
				},
				[COUNTRY.GRC]: {
					isoChar2: 'GR',
					isoChar3: 'GRC',
					isoNum: '300',
					partOfEu: true,
					vatIdRegEx: /^(EL)[0-9]{9}$/,
					systemWideSupport: true,
				},
				[COUNTRY.HUN]: {
					isoChar2: 'HU',
					isoChar3: 'HUN',
					isoNum: '348',
					partOfEu: true,
					vatIdRegEx: /^(HU)[0-9]{8}$/,
					systemWideSupport: false,
				},
				[COUNTRY.IRL]: {
					isoChar2: 'IE',
					isoChar3: 'IRL',
					isoNum: '372',
					partOfEu: true,
					vatIdRegEx: /^(IE)([0-9][A-Z][0-9]{5}[A-Z]|[0-9]{7}[A-Z]{1,2})$/,
					systemWideSupport: false,
				},
				[COUNTRY.ITA]: {
					isoChar2: 'IT',
					isoChar3: 'ITA',
					isoNum: '380',
					partOfEu: true,
					vatIdRegEx: /^(IT)[0-9]{11}$/,
					systemWideSupport: false,
				},
				[COUNTRY.LVA]: {
					isoChar2: 'LV',
					isoChar3: 'LVA',
					isoNum: '428',
					partOfEu: true,
					vatIdRegEx: /^(LV)[0-9]{11}$/,
					systemWideSupport: false,
				},
				[COUNTRY.LTU]: {
					isoChar2: 'LT',
					isoChar3: 'LTU',
					isoNum: '440',
					partOfEu: true,
					vatIdRegEx: /^(LT)([0-9]{9}|[0-9]{12})$/,
					systemWideSupport: false,
				},
				[COUNTRY.LUX]: {
					isoChar2: 'LU',
					isoChar3: 'LUX',
					isoNum: '442',
					partOfEu: true,
					vatIdRegEx: /^(LU)[0-9]{8}$/,
					systemWideSupport: false,
				},
				[COUNTRY.MLT]: {
					isoChar2: 'MT',
					isoChar3: 'MLT',
					isoNum: '470',
					partOfEu: true,
					vatIdRegEx: /^(MT)[0-9]{8}$/,
					systemWideSupport: false,
				},
				[COUNTRY.POL]: {
					isoChar2: 'PL',
					isoChar3: 'POL',
					isoNum: '616',
					partOfEu: true,
					vatIdRegEx: /^(PL)[0-9]{10}$/,
					systemWideSupport: false,
				},
				[COUNTRY.PRT]: {
					isoChar2: 'PT',
					isoChar3: 'PRT',
					isoNum: '620',
					partOfEu: true,
					vatIdRegEx: /^(PT)[0-9]{9}$/,
					systemWideSupport: false,
				},
				[COUNTRY.ROU]: {
					isoChar2: 'RO',
					isoChar3: 'ROU',
					isoNum: '642',
					partOfEu: true,
					vatIdRegEx: /^(RO)[0-9]{2,10}$/,
					systemWideSupport: false,
				},
				[COUNTRY.SVN]: {
					isoChar2: 'SI',
					isoChar3: 'SVN',
					isoNum: '705',
					partOfEu: true,
					vatIdRegEx: /^(SI)[0-9]{8}$/,
					systemWideSupport: false,
				},
				[COUNTRY.ESP]: {
					isoChar2: 'ES',
					isoChar3: 'ESP',
					isoNum: '724',
					partOfEu: true,
					vatIdRegEx: /^(ES)[0-9A-Z][0-9]{7}[0-9A-Z]$/,
					systemWideSupport: false,
				},
				[COUNTRY.SWE]: {
					isoChar2: 'SE',
					isoChar3: 'SWE',
					isoNum: '752',
					partOfEu: true,
					vatIdRegEx: /^(SE)[0-9]{12}$/,
					systemWideSupport: false,
				},
				[COUNTRY.NLD]: {
					isoChar2: 'NL',
					isoChar3: 'NLD',
					isoNum: '528',
					partOfEu: true,
					vatIdRegEx: /^(NL)[0-9]{9}B[0-9]{2}$/,
					systemWideSupport: false,
				},
				[COUNTRY.ISL]: {
					isoChar2: 'IS',
					isoChar3: 'ISL',
					isoNum: '352',
					partOfEu: false,
					systemWideSupport: false,
					vatIdRegEx: /^(IS)[0-9]{5,6}$/,
				},
				[COUNTRY.LIE]: {
					isoChar2: 'LI',
					isoChar3: 'LIE',
					isoNum: '438',
					partOfEu: false,
					systemWideSupport: false,
					vatIdRegEx: /^(LI)[0-9]{5}$/,
				},
				[COUNTRY.NOR]: {
					isoChar2: 'NO',
					isoChar3: 'NOR',
					isoNum: '578',
					partOfEu: false,
					systemWideSupport: false,
					vatIdRegEx: /^(NO)[0-9]{9}$/,
				},
				[COUNTRY.CHE]: {
					isoChar2: 'CH',
					isoChar3: 'CHE',
					isoNum: '756',
					partOfEu: false,
					systemWideSupport: false,
					vatIdRegEx: /^(CHE)[0-9]{9}(MWST|TVA|IVA)$/,
				},
			},
			'countryId',
			null,
		);
	}
}

export const countryEnumBase = new CountryEnumBase();
